/**
 * Issue #669 (J & N Hold Handling) — client-side J&N navigation-lock store
 * (@dylan219 2026-09-01: "remove this button and do not allow the agent
 * navigate off this Warning for J&N block only for now").
 *
 * A simultaneous J+N hold on an identified customer's accounts is a fraud/AML
 * HARD STOP. Until now the warning was a per-screen pop-up with an
 * "Acknowledge — cannot proceed" button that let the agent dismiss it and walk
 * to any other screen. The authoritative stop was ALWAYS the server-side
 * `requireNoJnHoldBlock` guard (every servicing/banking route 403s while the
 * block is recorded — untouched by this change); this store is the agent-facing
 * half that hoists the warning to the APP SHELL: whenever a J&N verdict is
 * raised (the Dashboard's synchronous accounts evaluation or the registration
 * screens' `/idv/auth-hold-check` verdict), it is published HERE, and App.tsx
 * renders the non-dismissible warning IN PLACE OF the routed screens — so on
 * ANY route the agent sees the warning and cannot navigate off it.
 *
 * Mirrors the tiny observable-store pattern of `duplicate-session-store.ts`
 * (no polling, no focus-trap libraries — deterministic + unit-testable). The
 * entry is KEYED to the identified customer's CIF so a stale verdict for one
 * customer can never lock the app for another; it is cleared on a new ID&V
 * (IdvScreen) and on Clear Session (App) — the same moments the SERVER drops
 * its block (`beginVerification` / clear-session), keeping the client lock
 * exactly as long as the server block and never longer.
 *
 * Scope note ("only for now"): this lock is J&N-only. The CCF-43 auth-hold
 * pop-up (`AuthHoldPopup`, issue #574 acknowledgement) keeps its dismiss
 * behaviour untouched.
 */

import type { JnHoldResult } from './lib/jn-hold.js';

/** The active J&N warning, keyed to the CIF it was raised for. */
export interface JnHoldBlock {
  /** Canonical CIF of the identified customer the verdict belongs to. */
  readonly customerNumber: string;
  /** The pop-up payload (affected accounts + fraud-transfer scripting). */
  readonly result: JnHoldResult;
}

type Listener = (block: JnHoldBlock | null) => void;

let current: JnHoldBlock | null = null;
const listeners = new Set<Listener>();

/** The currently-active J&N warning, or null when no block is raised. */
export function getJnHoldBlock(): JnHoldBlock | null {
  return current;
}

/** Record (or clear) the active J&N warning and notify subscribers. */
export function setJnHoldBlock(block: JnHoldBlock | null): void {
  current = block;
  for (const l of listeners) l(block);
}

/** Subscribe to J&N block changes; returns an unsubscribe fn. */
export function subscribeJnHoldBlock(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
